import { Head, Link, useForm } from "@inertiajs/react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ArrowLeft, Target, Calculator } from "lucide-react"
import { AppLayout } from "@/layouts/app-layout"
import { Breadcrumbs } from "@/components/breadcrumbs"
import { goalsPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Dashboard",
    href: "/dashboard",
  },
  {
    title: "Goals",
    href: goalsPath(),
  },
  {
    title: "Add Goal",
    href: "/goals/new",
  },
]

interface GoalData {
  target_amount: number
  current_amount: number
  goal_type: string
  target_date: string
}

interface GoalFormData {
  goal: GoalData
}

interface GoalsNewProps {
  goal: GoalData
  goal_types: Record<string, string>
  errors?: string[]
}

export default function GoalsNew({
  goal,
  goal_types,
  errors = [],
}: GoalsNewProps) {
  const { data, setData, post, processing } = useForm<GoalFormData>({
    goal: {
      target_amount: goal.target_amount || 0,
      current_amount: goal.current_amount || 0,
      goal_type: goal.goal_type || "",
      target_date: goal.target_date || "",
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(goalsPath())
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const calculateProgress = () => {
    if (data.goal.target_amount === 0) return 0
    return (data.goal.current_amount / data.goal.target_amount) * 100
  }

  const calculateRemaining = () => {
    return Math.max(0, data.goal.target_amount - data.goal.current_amount)
  }

  return (
    <AppLayout>
      <Head title="Add Goal" />

      <div className="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-6">
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href={goalsPath()}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Goals
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Create New Goal</h1>
            <p className="text-muted-foreground">
              Set clear financial targets to guide your FIRE journey
            </p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Goal Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Goal Type */}
                <div className="space-y-2">
                  <Label htmlFor="goal_type">Goal Type *</Label>
                  <Select
                    value={data.goal.goal_type}
                    onValueChange={(value) =>
                      setData("goal", {
                        ...data.goal,
                        goal_type: value,
                      })
                    }
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a goal type" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(goal_types).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Target Amount */}
                <div className="space-y-2">
                  <Label htmlFor="target_amount">Target Amount *</Label>
                  <div className="relative">
                    <div className="text-muted-foreground absolute top-2.5 left-3">
                      $
                    </div>
                    <Input
                      id="target_amount"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="10000.00"
                      className="pl-8"
                      value={data.goal.target_amount}
                      onChange={(e) =>
                        setData("goal", {
                          ...data.goal,
                          target_amount: parseFloat(e.target.value) || 0,
                        })
                      }
                      required
                    />
                  </div>
                </div>

                {/* Current Amount */}
                <div className="space-y-2">
                  <Label htmlFor="current_amount">Current Amount</Label>
                  <div className="relative">
                    <div className="text-muted-foreground absolute top-2.5 left-3">
                      $
                    </div>
                    <Input
                      id="current_amount"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      className="pl-8"
                      value={data.goal.current_amount}
                      onChange={(e) =>
                        setData("goal", {
                          ...data.goal,
                          current_amount: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                </div>

                {/* Target Date */}
                <div className="space-y-2">
                  <Label htmlFor="target_date">Target Date (Optional)</Label>
                  <Input
                    id="target_date"
                    type="date"
                    value={data.goal.target_date}
                    onChange={(e) =>
                      setData("goal", {
                        ...data.goal,
                        target_date: e.target.value,
                      })
                    }
                  />
                  <p className="text-muted-foreground text-xs">
                    When do you want to achieve this goal?
                  </p>
                </div>

                {/* Error Messages */}
                {errors.length > 0 && (
                  <div className="bg-destructive/15 rounded-md p-4">
                    <div className="flex">
                      <div className="ml-3">
                        <h3 className="text-destructive text-sm font-medium">
                          There were errors with your submission:
                        </h3>
                        <div className="text-destructive mt-2 text-sm">
                          <ul role="list" className="list-disc space-y-1 pl-5">
                            {errors.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex gap-4 pt-4">
                  <Button type="submit" disabled={processing}>
                    {processing ? "Creating..." : "Create Goal"}
                  </Button>
                  <Button variant="outline" asChild>
                    <Link href={goalsPath()}>Cancel</Link>
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Preview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5" />
                Goal Preview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {data.goal.target_amount > 0 && (
                <>
                  <div>
                    <Label className="text-sm font-medium">
                      Progress Overview
                    </Label>
                    <div className="mt-2 space-y-3">
                      <div className="flex justify-between text-sm">
                        <span>Current Progress</span>
                        <span className="font-medium">
                          {formatCurrency(data.goal.current_amount)} /{" "}
                          {formatCurrency(data.goal.target_amount)}
                        </span>
                      </div>
                      <div className="h-3 w-full rounded-full bg-gray-200">
                        <div
                          className="h-3 rounded-full bg-blue-600 transition-all duration-300"
                          style={{
                            width: `${Math.min(100, calculateProgress())}%`,
                          }}
                        />
                      </div>
                      <div className="text-muted-foreground text-center text-sm">
                        {calculateProgress().toFixed(1)}% Complete
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <div className="text-muted-foreground mb-1 text-xs">
                        Amount Remaining
                      </div>
                      <div className="text-lg font-bold">
                        {formatCurrency(calculateRemaining())}
                      </div>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <div className="text-muted-foreground mb-1 text-xs">
                        Monthly Target
                      </div>
                      <div className="text-lg font-bold">
                        {data.goal.target_date
                          ? formatCurrency(
                              calculateRemaining() /
                                Math.max(
                                  1,
                                  Math.ceil(
                                    (new Date(data.goal.target_date).getTime() -
                                      new Date().getTime()) /
                                      (1000 * 60 * 60 * 24 * 30),
                                  ),
                                ),
                            )
                          : formatCurrency(calculateRemaining() / 12)}
                      </div>
                    </div>
                  </div>

                  <div className="text-muted-foreground space-y-1 text-xs">
                    <p>
                      💡 <strong>Tip:</strong> Start small and build momentum
                      with regular contributions.
                    </p>
                    <p>
                      🎯 <strong>Goal:</strong>{" "}
                      {data.goal.goal_type
                        ? goal_types[data.goal.goal_type]
                        : "Select a goal type"}
                    </p>
                    {data.goal.target_date && (
                      <p>
                        📅 <strong>Deadline:</strong>{" "}
                        {new Date(data.goal.target_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </>
              )}

              {data.goal.target_amount === 0 && (
                <div className="py-8 text-center">
                  <Target className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
                  <p className="text-muted-foreground">
                    Enter a target amount to see your goal preview
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  )
}
